import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import adminApi from '../utils/adminApi';

// Lazy-load html5-qrcode only when the scanner is opened (keeps main bundle light)
const loadScanner = () => import('html5-qrcode');

// ------- Scanner modal -------
const QrScannerModal = ({ open, onClose, onDetect }) => {
  const containerId = 'admin-qr-reader';
  const scannerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    let active = true;
    (async () => {
      const { Html5Qrcode } = await loadScanner();
      if (!active) return;
      const scanner = new Html5Qrcode(containerId);
      scannerRef.current = scanner;
      try {
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decodedText) => {
            onDetect(decodedText.trim());
          },
          () => {},
        );
      } catch (err) {
        toast.error('Camera not available — please use the code entry.');
        onClose();
      }
    })();
    return () => {
      active = false;
      const s = scannerRef.current;
      if (s) {
        s.stop().then(() => s.clear()).catch(() => {});
      }
    };
  }, [open, onClose, onDetect]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 rounded-3xl overflow-hidden max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 flex items-center justify-between bg-slate-800">
          <h3 className="font-bold text-white">Scan customer QR</h3>
          <button onClick={onClose} className="text-slate-300 hover:text-white" data-testid="scanner-close-btn">
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>
        <div id={containerId} className="w-full bg-black" style={{ minHeight: 320 }}></div>
        <p className="p-3 text-center text-xs text-slate-400">Point the camera at the customer's QR code</p>
      </div>
    </div>
  );
};

// ------- Main Rewards admin page -------
const RewardsAdmin = () => {
  const navigate = useNavigate();
  const [rewards, setRewards] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [top, setTop] = useState([]);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [awardEuro, setAwardEuro] = useState('');
  const [awardPts, setAwardPts] = useState('');
  const [awardNote, setAwardNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null); // reward being edited, or {} for new
  const [formVal, setFormVal] = useState({ title: '', description: '', points_cost: '', emoji: '', active: true });

  const loadRewards = async () => {
    try {
      const r = await adminApi.get('/admin/rewards/menu');
      setRewards(r.data?.rewards || []);
    } catch { toast.error('Failed to load rewards menu'); }
  };
  const loadTop = async () => {
    try {
      const r = await adminApi.get('/admin/wallet/top?limit=10');
      setTop(r.data?.wallets || []);
    } catch {}
  };

  useEffect(() => {
    loadRewards();
    loadTop();
  }, []);

  const lookup = async (codeOrId) => {
    if (!codeOrId) return;
    setBusy(true);
    try {
      const r = await adminApi.get(`/admin/wallet/lookup/${encodeURIComponent(codeOrId)}`);
      setWallet(r.data?.wallet);
      toast.success(`Wallet found · ${r.data.wallet.points} pts`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Wallet not found');
      setWallet(null);
    } finally {
      setBusy(false);
    }
  };

  const onScanDetect = async (text) => {
    setScannerOpen(false);
    await lookup(text);
  };

  const doAward = async () => {
    if (!wallet) { toast.error('Look up a wallet first'); return; }
    const body = {};
    if (awardPts && Number(awardPts) > 0) body.points = Number(awardPts);
    else if (awardEuro && Number(awardEuro) > 0) body.amount_eur = Number(awardEuro);
    else { toast.error('Enter Euro amount or points'); return; }
    if (awardNote.trim()) body.note = awardNote.trim();
    setBusy(true);
    try {
      const r = await adminApi.post(`/admin/wallet/${wallet.id}/earn`, body);
      setWallet(r.data.wallet);
      toast.success(`+${r.data.earned} pts · Total: ${r.data.wallet.points}`);
      setAwardEuro(''); setAwardPts(''); setAwardNote('');
      loadTop();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Award failed');
    } finally {
      setBusy(false);
    }
  };

  const doRedeem = async (reward) => {
    if (!wallet) { toast.error('Look up a wallet first'); return; }
    if (wallet.points < reward.points_cost) {
      toast.error(`Not enough points (${wallet.points}/${reward.points_cost})`);
      return;
    }
    if (!window.confirm(`Redeem "${reward.title}" for ${reward.points_cost} pts?`)) return;
    setBusy(true);
    try {
      const r = await adminApi.post(`/admin/wallet/${wallet.id}/redeem`, { reward_id: reward.id });
      setWallet(r.data.wallet);
      toast.success(`Redeemed: ${r.data.redeemed}`);
      loadTop();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Redeem failed');
    } finally {
      setBusy(false);
    }
  };

  const openEditor = (reward) => {
    if (reward) {
      setEditing(reward);
      setFormVal({
        title: reward.title || '',
        description: reward.description || '',
        points_cost: String(reward.points_cost || ''),
        emoji: reward.emoji || '',
        active: !!reward.active,
      });
    } else {
      setEditing({});
      setFormVal({ title: '', description: '', points_cost: '', emoji: '🎁', active: true });
    }
    setShowEditor(true);
  };

  const saveReward = async (e) => {
    e?.preventDefault();
    const body = {
      title: formVal.title.trim(),
      description: formVal.description.trim() || undefined,
      points_cost: Number(formVal.points_cost),
      emoji: formVal.emoji.trim() || undefined,
      active: !!formVal.active,
    };
    if (!body.title) { toast.error('Title required'); return; }
    if (!body.points_cost || body.points_cost < 1) { toast.error('Points cost must be at least 1'); return; }
    setBusy(true);
    try {
      if (editing?.id) {
        await adminApi.patch(`/admin/rewards/menu/${editing.id}`, body);
        toast.success('Reward updated');
      } else {
        await adminApi.post('/admin/rewards/menu', body);
        toast.success('Reward added');
      }
      setShowEditor(false);
      await loadRewards();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const deleteReward = async (reward) => {
    if (!window.confirm(`Delete "${reward.title}"?`)) return;
    try {
      await adminApi.delete(`/admin/rewards/menu/${reward.id}`);
      toast.success('Deleted');
      await loadRewards();
    } catch {
      toast.error('Delete failed');
    }
  };

  const toggleActive = async (reward) => {
    try {
      await adminApi.patch(`/admin/rewards/menu/${reward.id}`, { active: !reward.active });
      await loadRewards();
    } catch {
      toast.error('Toggle failed');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="rewards-admin-page">
      <div className="bg-slate-900/80 backdrop-blur border-b border-slate-800 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/staff')}
              className="w-10 h-10 rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center justify-center transition"
              data-testid="rewards-back-btn"
            >
              <i className="fas fa-arrow-left"></i>
            </button>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-amber-400">Admin</p>
              <h1 className="text-2xl font-black tracking-tight">Rewards</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-8">
        {/* LEFT: Scan / Lookup / Award / Redeem */}
        <div className="space-y-6">
          <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <i className="fas fa-qrcode text-amber-400"></i>
              <h2 className="text-lg font-bold">Find customer wallet</h2>
            </div>
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setScannerOpen(true)}
                className="flex-1 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-slate-900 font-black rounded-xl inline-flex items-center justify-center gap-2"
                data-testid="rewards-scan-btn"
              >
                <i className="fas fa-camera"></i> Scan QR
              </button>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value.toUpperCase())}
                placeholder="Or type 6-char code"
                className="flex-1 px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm tracking-widest font-mono focus:border-amber-500 focus:outline-none"
                maxLength={40}
                data-testid="rewards-code-input"
              />
              <button
                onClick={() => lookup(manualCode)}
                disabled={busy || !manualCode.trim()}
                className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 rounded-lg font-semibold"
                data-testid="rewards-lookup-btn"
              >
                Find
              </button>
            </div>
          </div>

          {wallet && (
            <div className="bg-slate-900/70 border border-amber-500/40 rounded-2xl p-6 space-y-4" data-testid="rewards-wallet-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-amber-400">Wallet</p>
                  <p className="text-3xl font-black">{wallet.short_code}</p>
                  {wallet.phone && <p className="text-sm text-slate-400 mt-0.5">{wallet.phone}{wallet.name ? ` · ${wallet.name}` : ''}</p>}
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Balance</p>
                  <p className="text-4xl font-black text-amber-400" data-testid="wallet-balance-display">{wallet.points}</p>
                  <p className="text-xs text-slate-500">Lifetime: {wallet.lifetime_earned}</p>
                </div>
              </div>

              {/* Award points */}
              <div className="border-t border-slate-800 pt-4">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Award points</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-1">Amount (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={awardEuro}
                      onChange={(e) => { setAwardEuro(e.target.value); setAwardPts(''); }}
                      placeholder="42.50"
                      className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm focus:border-amber-500 focus:outline-none"
                      data-testid="award-euro-input"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-1">or Points</label>
                    <input
                      type="number"
                      min="0"
                      value={awardPts}
                      onChange={(e) => { setAwardPts(e.target.value); setAwardEuro(''); }}
                      placeholder="10"
                      className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm focus:border-amber-500 focus:outline-none"
                      data-testid="award-pts-input"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500 block mb-1">Note (opt.)</label>
                    <input
                      type="text"
                      value={awardNote}
                      onChange={(e) => setAwardNote(e.target.value)}
                      placeholder="Sale #123"
                      className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm focus:border-amber-500 focus:outline-none"
                    />
                  </div>
                </div>
                <button
                  onClick={doAward}
                  disabled={busy || (!awardEuro && !awardPts)}
                  className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 rounded-lg font-bold inline-flex items-center justify-center gap-2"
                  data-testid="award-btn"
                >
                  <i className="fas fa-plus-circle"></i> Add points
                </button>
              </div>

              {/* Redeem */}
              <div className="border-t border-slate-800 pt-4">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Redeem a reward</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {rewards.filter((r) => r.active).map((r) => {
                    const affordable = wallet.points >= r.points_cost;
                    return (
                      <button
                        key={r.id}
                        onClick={() => doRedeem(r)}
                        disabled={busy || !affordable}
                        className={`text-left px-3 py-2 rounded-lg border transition ${affordable ? 'bg-slate-800 border-slate-700 hover:border-amber-500' : 'bg-slate-900 border-slate-800 opacity-50 cursor-not-allowed'}`}
                        data-testid={`redeem-${r.id}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-semibold truncate">{r.emoji || '🎁'} {r.title}</span>
                          <span className="text-xs font-black text-amber-400 shrink-0">{r.points_cost}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {wallet.history && wallet.history.length > 0 && (
                <div className="border-t border-slate-800 pt-4">
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Recent activity</p>
                  <ul className="space-y-1 max-h-40 overflow-y-auto">
                    {wallet.history.slice(0, 5).map((h) => (
                      <li key={h.id} className="flex items-center justify-between text-sm">
                        <span className="truncate text-slate-300">{h.note}</span>
                        <span className={`font-bold ${h.type === 'earn' ? 'text-emerald-400' : 'text-rose-400'}`}>
                          {h.type === 'earn' ? '+' : ''}{h.amount}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT: Rewards menu editor + top customers */}
        <div className="space-y-6">
          <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <i className="fas fa-gift text-amber-400"></i>
                <h2 className="text-lg font-bold">Rewards menu</h2>
              </div>
              <button
                onClick={() => openEditor(null)}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-amber-500 hover:bg-amber-400 text-slate-900 text-sm font-bold rounded-lg"
                data-testid="add-reward-btn"
              >
                <i className="fas fa-plus"></i> Add
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-3">Customers see active rewards live on their wallet page.</p>
            {rewards.length === 0 ? (
              <p className="text-slate-500 text-sm italic text-center py-6">No rewards yet — click Add to create one.</p>
            ) : (
              <ul className="space-y-2" data-testid="admin-rewards-list">
                {rewards.map((r) => (
                  <li key={r.id} className={`flex items-center gap-3 p-3 rounded-lg border transition ${r.active ? 'bg-slate-800/60 border-slate-700' : 'bg-slate-900/40 border-slate-800 opacity-60'}`} data-testid={`admin-reward-${r.id}`}>
                    <div className="text-2xl w-8 text-center">{r.emoji || '🎁'}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold truncate">{r.title}</p>
                      {r.description && <p className="text-xs text-slate-500 truncate">{r.description}</p>}
                    </div>
                    <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 text-xs font-black rounded-full">{r.points_cost}</span>
                    <button onClick={() => toggleActive(r)} title={r.active ? 'Hide from customers' : 'Show to customers'} className="text-slate-400 hover:text-amber-400 p-1" data-testid={`toggle-reward-${r.id}`}>
                      <i className={`fas ${r.active ? 'fa-eye' : 'fa-eye-slash'}`}></i>
                    </button>
                    <button onClick={() => openEditor(r)} className="text-slate-400 hover:text-white p-1" data-testid={`edit-reward-${r.id}`}>
                      <i className="fas fa-pen"></i>
                    </button>
                    <button onClick={() => deleteReward(r)} className="text-slate-500 hover:text-red-400 p-1" data-testid={`delete-reward-${r.id}`}>
                      <i className="fas fa-trash"></i>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <i className="fas fa-trophy text-amber-400"></i>
              <h2 className="text-lg font-bold">Top customers</h2>
            </div>
            {top.length === 0 ? (
              <p className="text-slate-500 text-sm italic">No customer wallets yet.</p>
            ) : (
              <ul className="space-y-1" data-testid="top-customers-list">
                {top.map((w, i) => (
                  <li key={w.id} className="flex items-center gap-3 py-2 border-b border-slate-800/60 last:border-0">
                    <span className="w-6 text-center text-xs font-black text-slate-500">#{i + 1}</span>
                    <span className="font-mono font-bold text-sm flex-1 min-w-0 truncate">{w.short_code}{w.name ? ` · ${w.name}` : ''}</span>
                    <span className="text-xs text-slate-400">{w.points} pts</span>
                    <span className="text-[10px] text-slate-600">({w.lifetime_earned} total)</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* QR scanner overlay */}
      <QrScannerModal open={scannerOpen} onClose={() => setScannerOpen(false)} onDetect={onScanDetect} />

      {/* Editor modal */}
      {showEditor && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowEditor(false)}>
          <form onSubmit={saveReward} className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-black">{editing?.id ? 'Edit reward' : 'New reward'}</h3>
            <div className="grid grid-cols-[80px_1fr] gap-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Emoji</label>
                <input
                  type="text"
                  value={formVal.emoji}
                  onChange={(e) => setFormVal({ ...formVal, emoji: e.target.value })}
                  placeholder="🎁"
                  className="w-full mt-1 px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-2xl text-center focus:border-amber-500 focus:outline-none"
                  maxLength={4}
                  data-testid="reward-emoji-input"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Title *</label>
                <input
                  type="text"
                  value={formVal.title}
                  onChange={(e) => setFormVal({ ...formVal, title: e.target.value })}
                  placeholder="€2 off shop item"
                  className="w-full mt-1 px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm focus:border-amber-500 focus:outline-none"
                  required
                  data-testid="reward-title-input"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Description (optional)</label>
              <input
                type="text"
                value={formVal.description}
                onChange={(e) => setFormVal({ ...formVal, description: e.target.value })}
                placeholder="Any accessory under €20"
                className="w-full mt-1 px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm focus:border-amber-500 focus:outline-none"
                data-testid="reward-desc-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Points cost *</label>
                <input
                  type="number"
                  min="1"
                  value={formVal.points_cost}
                  onChange={(e) => setFormVal({ ...formVal, points_cost: e.target.value })}
                  placeholder="150"
                  className="w-full mt-1 px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-lg text-sm focus:border-amber-500 focus:outline-none"
                  required
                  data-testid="reward-cost-input"
                />
              </div>
              <label className="flex items-end gap-2 cursor-pointer pb-2.5">
                <input
                  type="checkbox"
                  checked={formVal.active}
                  onChange={(e) => setFormVal({ ...formVal, active: e.target.checked })}
                  className="w-4 h-4 accent-amber-500"
                  data-testid="reward-active-toggle"
                />
                <span className="text-sm font-semibold">Visible to customers</span>
              </label>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setShowEditor(false)} className="flex-1 py-2.5 bg-slate-800 hover:bg-slate-700 rounded-lg font-semibold">Cancel</button>
              <button type="submit" disabled={busy} className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-900 rounded-lg font-black disabled:opacity-60" data-testid="reward-save-btn">
                {busy ? <i className="fas fa-spinner fa-spin"></i> : (editing?.id ? 'Save' : 'Add reward')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default RewardsAdmin;
